/**
 * 학습 기록 서비스
 * 학습 기록 목록 조회 기능 제공
 */

import apiClient from '../../core/http/axios';

// 학습 기록 데이터 타입
export interface LearningRecordData {
  record_uuid: string;
  user_no: number;
  book_no: number;
  scope: 'BOOK' | 'PAGE';
  page_no: number | null;
  anchor_page_no: number | null;
  page_idx: number | null;
  page_display_number: number | null;
  record_type: 'AUDIO' | 'ANNOTATION' | 'NOTE' | 'QUIZ' | 'DIALOG' | 'SHARED_FOLDER';
  visibility: 'PRIVATE' | 'PUBLIC';
  title: string;
  summary: string;
  annotation_uuid: string | null;
  dialog_no: number | null;
  dialog_uuid: string | null;
  audio_id: string | null;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
}

// API 응답 타입
interface LearningRecordListResponse {
  success: boolean;
  error: string | null;
  data: {
    total: number;
    limit: number;
    offset: number;
    items: LearningRecordData[];
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/**
 * 학습 기록 서비스
 */
export const learningRecordService = {
  /**
   * 학습 기록 목록 조회
   * @param bookUuid 책 UUID
   * @param limit 조회 개수 (기본값: 50)
   * @param offset 시작 위치 (기본값: 0)
   * @returns 학습 기록 목록과 메타데이터
   */
  getLearningRecords: async (
    bookUuid: string,
    limit: number = 50,
    offset: number = 0,
    extraHeaders?: Record<string, string>
  ): Promise<{ items: LearningRecordData[]; total: number; limit: number; offset: number }> => {
    try {
      const response = await apiClient.get<LearningRecordListResponse>(
        `/viewer/learning-records`,
        {
          params: {
            book_uuid: bookUuid,
            limit,
            offset,
          },
          ...(extraHeaders ? { headers: extraHeaders } : {}),
        }
      );

      if (!response.data.success) {
        // console.error('[LearningRecord] 목록 조회 실패:', response.data.error);
        return {
          items: [],
          total: 0,
          limit: limit,
          offset: offset,
        };
      }

      const data = response.data.data || { items: [], total: 0, limit: limit, offset: offset };
      const items = data.items || [];
      return {
        items,
        total: data.total,
        limit: data.limit,
        offset: data.offset,
      };
    } catch (error) {
      // console.error('[LearningRecord] 목록 조회 오류:', error);
      return {
        items: [],
        total: 0,
        limit: limit,
        offset: offset,
      };
    }
  },

  /**
   * 날짜를 시간 범위 형식으로 변환
   * @param createdAt 생성 시간
   * @param updatedAt 수정 시간
   */
  formatTimeRange: (createdAt: string, updatedAt: string): string => {
    try {
      const created = new Date(createdAt);
      const updated = new Date(updatedAt);
      
      const formatTime = (date: Date): string => {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      };
      
      return `${formatTime(created)}-${formatTime(updated)}`;
    } catch (error) {
      // console.error('[LearningRecord] 시간 범위 포맷 실패:', error);
      return '';
    }
  },
};

export default learningRecordService;
